import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  LocalShipping as SupplierIcon,
  SwapHoriz as MovementIcon,
  MenuBook as RecipeIcon,
  Assessment as ReportIcon,
  People as UsersIcon,
  Restaurant as RestaurantIcon,
  PointOfSale as POSIcon,
  Warehouse as WarehouseIcon,
  Person as CustomerIcon,
  Security as SecurityIcon,
  History as AuditIcon,
  Straighten as UnitIcon,
  Kitchen as StationIcon,
  Receipt as FacturacionIcon,
  ReceiptLong as TicketIcon,
  Sensors as LiveIcon,
  PointOfSale as CajaIcon,
  History as CashHistoryIcon,
  AccountBalanceWallet as WalletIcon,
  TableRestaurant as TableIcon,
  ExpandLess,
  ExpandMore,
  FiberManualRecord as DotIcon,
  RoomService as ComandaIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { ordersApi } from '../../api/orders.api';

const DRAWER_WIDTH = 260;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant: 'permanent' | 'temporary';
}

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'POS (Pedidos)', icon: <POSIcon />, path: '/pos' },
  { text: 'Cocina / Bar (vivo)', icon: <LiveIcon />, path: '/estacion' },
  // Solo visible en modo "comanda al mozo" (ver flowMode abajo).
  { text: 'Comandas (mozo)', icon: <ComandaIcon />, path: '/comandas' },
  { text: 'Caja', icon: <CajaIcon />, path: '/caja' },
  // 'Mesas' es un item especial que se renderiza con su propio grupo expandible
  // (Salon / Patio / Otros). Ver renderMesasGroup() abajo.
  { text: 'Productos', icon: <InventoryIcon />, path: '/productos' },
  { text: 'Categorias', icon: <CategoryIcon />, path: '/categorias' },
  { text: 'Unidades', icon: <UnitIcon />, path: '/unidades' },
  { text: 'Proveedores', icon: <SupplierIcon />, path: '/proveedores' },
  { text: 'Movimientos', icon: <MovementIcon />, path: '/movimientos' },
  { text: 'Recetas', icon: <RecipeIcon />, path: '/recetas' },
  { text: 'Estaciones (Bar/Cocina)', icon: <StationIcon />, path: '/estaciones' },
  { text: 'Tickets', icon: <TicketIcon />, path: '/tickets' },
  { text: 'Bodegas', icon: <WarehouseIcon />, path: '/bodegas' },
  { text: 'Cuentas por Cobrar', icon: <WalletIcon />, path: '/cuentas-por-cobrar' },
  { text: 'Reportes', icon: <ReportIcon />, path: '/reportes' },
];

const adminItems = [
  { text: 'Historial Cajas', icon: <CashHistoryIcon />, path: '/historial-cajas' },
  { text: 'Facturacion', icon: <FacturacionIcon />, path: '/facturacion' },
  { text: 'Clientes', icon: <CustomerIcon />, path: '/clientes' },
  { text: 'Usuarios', icon: <UsersIcon />, path: '/usuarios' },
  { text: 'Control Anti-Robo', icon: <SecurityIcon />, path: '/antirobo' },
  { text: 'Auditoria', icon: <AuditIcon />, path: '/auditoria' },
];

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, variant }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleNavigate = (path: string) => {
    navigate(path);
    if (variant === 'temporary') onClose();
  };

  const renderMenuItems = (items: typeof menuItems) =>
    items.map((item) => {
      // Para items con query string (ej /mesas?zone=salon), comparar pathname
      // exacto y query exacto. Para items sin query, marcar activo solo si la
      // URL actual NO tiene query — asi no quedan dos items resaltados a la vez.
      const [itemPath, itemQuery] = item.path.split('?');
      let isActive: boolean;
      if (itemQuery) {
        isActive = location.pathname === itemPath && location.search === `?${itemQuery}`;
      } else {
        const matchesPath =
          location.pathname === itemPath ||
          (itemPath !== '/' && location.pathname.startsWith(itemPath));
        isActive = matchesPath && location.search === '';
      }
      return (
        <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => handleNavigate(item.path)}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              backgroundColor: isActive ? 'rgba(79, 70, 229, 0.3)' : 'transparent',
              '&:hover': {
                backgroundColor: isActive
                  ? 'rgba(79, 70, 229, 0.4)'
                  : 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive ? '#818CF8' : '#9CA3AF',
                minWidth: 40,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? '#FFFFFF' : '#D1D5DB',
              }}
            />
          </ListItemButton>
        </ListItem>
      );
    });

  const roleLabel: Record<string, string> = {
    admin: 'Admin',
    mozo: 'Mozo',
    cajero: 'Cajero',
    almacen: 'Almacen',
    cocinero: 'Cocinero',
    barman: 'Barman',
  };

  const roleColor: Record<string, string> = {
    admin: '#7C3AED',
    mozo: '#059669',
    cajero: '#D97706',
    almacen: '#2563EB',
    cocinero: '#DC2626',
    barman: '#0EA5E9',
  };

  // Menu reducido por rol: cada perfil solo ve lo necesario para su funcion.
  // Mozo: solo POS (toma pedidos). Cajero: solo Caja. Cocinero: estacion en vivo + tickets.
  // En modo "comanda al mozo" el cajero registra pedidos en el POS y el mozo
  // confirma entregas en Comandas. En modo tradicional el menu no cambia.
  const [flowMode, setFlowMode] = useState<'tradicional' | 'mozo'>('tradicional');
  useEffect(() => {
    ordersApi.getFlowConfig().then((r) => setFlowMode(r.data.mode)).catch(() => {});
  }, []);
  const isMozoMode = flowMode === 'mozo';

  const ROLE_PATHS: Record<string, string[]> = {
    mozo: isMozoMode ? ['/pos', '/comandas'] : ['/pos'],
    barman: ['/estacion', '/tickets'],
    cocinero: ['/estacion', '/tickets'],
    cajero: isMozoMode ? ['/caja', '/pos', '/comandas'] : ['/caja'],
  };
  const allowed = ROLE_PATHS[user?.role || ''];
  const flowMenuItems = isMozoMode ? menuItems : menuItems.filter((m) => m.path !== '/comandas');
  const visibleMenuItems = allowed
    ? flowMenuItems
        .filter((m) => allowed.includes(m.path))
        .map((m) =>
          m.path === '/estacion'
            ? { ...m, text: user?.role === 'cocinero' ? 'Cocina (vivo)' : 'Bar (vivo)' }
            : m,
        )
    : flowMenuItems;

  // El grupo "Mesas" se renderiza separado del listado plano para mostrar las
  // zonas como sub-items expandibles. Solo se muestra para roles que tienen
  // acceso a /mesas (admin y resto sin restriccion explicita).
  const canSeeMesas = !allowed; // mozo/cajero/cocinero/barman ya tienen ROLE_PATHS sin /mesas

  // Auto-abrir si estamos dentro de /mesas
  const [mesasOpen, setMesasOpen] = useState(location.pathname.startsWith('/mesas'));
  useEffect(() => {
    if (location.pathname.startsWith('/mesas')) setMesasOpen(true);
  }, [location.pathname]);

  const mesasSubs = [
    { text: 'Salon', zone: 'salon' },
    { text: 'Patio', zone: 'patio' },
    { text: 'Otros', zone: 'otros' },
  ];

  const currentZone = new URLSearchParams(location.search).get('zone') || '';

  const mesasGroup = canSeeMesas ? (
    <>
      <ListItem disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          onClick={() => setMesasOpen((v) => !v)}
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            backgroundColor: location.pathname === '/mesas' && !currentZone ? 'rgba(79, 70, 229, 0.3)' : 'transparent',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
          }}
        >
          <ListItemIcon
            sx={{
              color: location.pathname.startsWith('/mesas') ? '#818CF8' : '#9CA3AF',
              minWidth: 40,
            }}
          >
            <TableIcon />
          </ListItemIcon>
          <ListItemText
            primary="Mesas"
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: location.pathname.startsWith('/mesas') ? 600 : 400,
              color: location.pathname.startsWith('/mesas') ? '#FFFFFF' : '#D1D5DB',
            }}
          />
          {mesasOpen ? <ExpandLess sx={{ color: '#9CA3AF' }} /> : <ExpandMore sx={{ color: '#9CA3AF' }} />}
        </ListItemButton>
      </ListItem>
      <Collapse in={mesasOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigate('/mesas')}
              sx={{
                borderRadius: 2,
                pl: 5, pr: 2, py: 0.75,
                backgroundColor: location.pathname === '/mesas' && !currentZone ? 'rgba(79, 70, 229, 0.3)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
              }}
            >
              <ListItemIcon sx={{ color: '#9CA3AF', minWidth: 28 }}>
                <DotIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText
                primary="Todas"
                primaryTypographyProps={{
                  fontSize: '0.85rem',
                  fontWeight: location.pathname === '/mesas' && !currentZone ? 600 : 400,
                  color: location.pathname === '/mesas' && !currentZone ? '#FFFFFF' : '#D1D5DB',
                }}
              />
            </ListItemButton>
          </ListItem>
          {mesasSubs.map((sub) => {
            const active = location.pathname === '/mesas' && currentZone === sub.zone;
            return (
              <ListItem key={sub.zone} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigate(`/mesas?zone=${sub.zone}`)}
                  sx={{
                    borderRadius: 2,
                    pl: 5, pr: 2, py: 0.75,
                    backgroundColor: active ? 'rgba(79, 70, 229, 0.3)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                  }}
                >
                  <ListItemIcon sx={{ color: active ? '#818CF8' : '#9CA3AF', minWidth: 28 }}>
                    <DotIcon sx={{ fontSize: 8 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={sub.text}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: active ? 600 : 400,
                      color: active ? '#FFFFFF' : '#D1D5DB',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </>
  ) : null;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <RestaurantIcon sx={{ fontSize: 32, color: '#818CF8' }} />
        <Box>
          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, lineHeight: 1.2 }}>
            Restaurante
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            Sistema de Gestion
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#374151' }} />

      <List sx={{ flex: 1, px: 1.5, py: 1, overflow: 'auto' }}>
        {/* Items previos a Mesas (Dashboard, POS, Cocina, Caja) */}
        {renderMenuItems(visibleMenuItems.slice(0, 4))}
        {/* Grupo Mesas con sub-items expandibles */}
        {mesasGroup}
        {/* Items posteriores a Mesas (Productos, Categorias, ...) */}
        {renderMenuItems(visibleMenuItems.slice(4))}

        {user?.role === 'admin' && (
          <>
            <Divider sx={{ borderColor: '#374151', my: 1 }} />
            <Typography variant="caption" sx={{ color: '#6B7280', px: 2, display: 'block', mb: 0.5 }}>
              Administracion
            </Typography>
            {renderMenuItems(adminItems)}
          </>
        )}
      </List>

      <Divider sx={{ borderColor: '#374151' }} />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: '#4F46E5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.85rem' }}>
            {user?.fullName?.charAt(0).toUpperCase() || 'U'}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ color: '#FFFFFF', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {user?.fullName || 'Usuario'}
          </Typography>
          <Chip
            label={roleLabel[user?.role || ''] || user?.role}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              backgroundColor: roleColor[user?.role || ''] || '#059669',
              color: '#FFFFFF',
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#111827',
          borderRight: '1px solid #1F2937',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
export { DRAWER_WIDTH };
